import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  Modal,
  TouchableOpacity,
  TextInput,
  Platform,
} from 'react-native';
import Feather from '@expo/vector-icons/Feather';
import MaterialCommunityIcons from '@expo/vector-icons/MaterialCommunityIcons';
import {
  Paprika,
  PaprikaTypography,
  PaprikaSpacing,
  PaprikaRadius,
  PaprikaShadows,
} from '../../styles/theme';
import { useTranslation } from 'react-i18next';

interface AddCustomMealModalProps {
  visible: boolean;
  slotLabel?: string; // e.g. "Thursday Dinner"
  onClose: () => void;
  onAdd: (text: string) => void;
}

// `labelKey` rather than a literal: the label is BOTH the chip text and the
// value stored on the meal, so it has to be resolved through t() in each place.
const QUICK_OPTIONS = [
  { labelKey: 'thisWeek.quickRestaurant', icon: 'silverware-fork-knife' as const, iconLib: 'mdi' as const },
  { labelKey: 'thisWeek.quickLeftovers', icon: 'package-variant' as const, iconLib: 'mdi' as const },
  { labelKey: 'thisWeek.quickTakeout', icon: 'shopping-outline' as const, iconLib: 'mdi' as const },
  { labelKey: 'thisWeek.quickSkip', icon: 'close-circle-outline' as const, iconLib: 'mdi' as const },
];

const AddCustomMealModal: React.FC<AddCustomMealModalProps> = ({
  visible,
  slotLabel,
  onClose,
  onAdd,
}) => {
  const { t } = useTranslation();
  const [text, setText] = useState('');

  // Reset when modal opens
  useEffect(() => {
    if (visible) {
      setText('');
    }
  }, [visible]);

  const handleAdd = () => {
    if (text.trim()) {
      onAdd(text.trim());
      setText('');
    }
  };

  const handleQuickOption = (label: string) => {
    onAdd(label);
    setText('');
  };

  return (
    <Modal
      visible={visible}
      transparent
      animationType="fade"
      onRequestClose={onClose}
    >
      <TouchableOpacity
        style={styles.overlay}
        activeOpacity={1}
        onPress={onClose}
      >
        <TouchableOpacity
          activeOpacity={1}
          onPress={(e) => e.stopPropagation()}
          style={styles.modalContent}
        >
          {/* Header */}
          <View style={styles.header}>
            <View>
              <Text style={styles.title}>{t('thisWeek.addCustomMeal')}</Text>
              {!!slotLabel && (
                <Text style={styles.subtitle}>{slotLabel}</Text>
              )}
            </View>
            <TouchableOpacity
              onPress={onClose}
              hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
            >
              <Feather name="x" size={20} color={Paprika.textMuted} />
            </TouchableOpacity>
          </View>

          {/* Quick options */}
          <View style={styles.quickOptions}>
            {QUICK_OPTIONS.map((option) => (
              <TouchableOpacity
                key={option.labelKey}
                style={styles.quickOption}
                onPress={() => handleQuickOption(t(option.labelKey))}
                activeOpacity={0.7}
              >
                <MaterialCommunityIcons
                  name={option.icon}
                  size={20}
                  color={Paprika.accent}
                />
                <Text style={styles.quickOptionText}>{t(option.labelKey)}</Text>
              </TouchableOpacity>
            ))}
          </View>

          {/* Divider with "or" */}
          <View style={styles.dividerRow}>
            <View style={styles.dividerLine} />
            <Text style={styles.dividerText}>{t('thisWeek.orTypeYourOwn')}</Text>
            <View style={styles.dividerLine} />
          </View>

          {/* Text input */}
          <View style={styles.inputRow}>
            <TextInput
              style={styles.input}
              placeholder={t('thisWeek.customMealPlaceholder')}
              placeholderTextColor={Paprika.textMuted}
              value={text}
              onChangeText={setText}
              autoFocus={false}
              returnKeyType="done"
              onSubmitEditing={handleAdd}
            />
          </View>

          {/* Action buttons */}
          <View style={styles.actions}>
            <TouchableOpacity
              style={styles.cancelButton}
              onPress={onClose}
            >
              <Text style={styles.cancelButtonText}>{t('common.cancel')}</Text>
            </TouchableOpacity>

            <TouchableOpacity
              style={[
                styles.addButton,
                !text.trim() && styles.addButtonDisabled,
              ]}
              onPress={handleAdd}
              disabled={!text.trim()}
            >
              <Text
                style={[
                  styles.addButtonText,
                  !text.trim() && styles.addButtonTextDisabled,
                ]}
              >
                {t('thisWeek.addHere')}
              </Text>
            </TouchableOpacity>
          </View>
        </TouchableOpacity>
      </TouchableOpacity>
    </Modal>
  );
};

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: PaprikaSpacing.lg,
  },
  modalContent: {
    backgroundColor: Paprika.background,
    borderRadius: PaprikaRadius.lg,
    width: '100%',
    maxWidth: 400,
    ...Platform.select({
      ios: {
        ...PaprikaShadows.modal,
      },
      android: {
        elevation: 8,
      },
      web: {
        ...PaprikaShadows.modal,
      },
    }),
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    paddingHorizontal: PaprikaSpacing.lg,
    paddingTop: PaprikaSpacing.lg,
    paddingBottom: PaprikaSpacing.md,
  },
  title: {
    ...PaprikaTypography.headingMedium,
    color: Paprika.textPrimary,
  },
  subtitle: {
    ...PaprikaTypography.bodyMedium,
    color: Paprika.textSecondary,
    marginTop: 2,
  },

  // Quick options
  quickOptions: {
    flexDirection: 'row',
    paddingHorizontal: PaprikaSpacing.lg,
    gap: PaprikaSpacing.sm,
  },
  quickOption: {
    flex: 1,
    alignItems: 'center',
    paddingVertical: PaprikaSpacing.md,
    borderRadius: PaprikaRadius.md,
    backgroundColor: Paprika.surfaceLight,
    gap: PaprikaSpacing.xs,
  },
  quickOptionText: {
    ...PaprikaTypography.labelMedium,
    color: Paprika.textPrimary,
  },

  // Divider
  dividerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: PaprikaSpacing.lg,
    paddingVertical: PaprikaSpacing.md,
    gap: PaprikaSpacing.sm,
  },
  dividerLine: {
    flex: 1,
    height: 1,
    backgroundColor: Paprika.borderLight,
  },
  dividerText: {
    ...PaprikaTypography.caption,
    color: Paprika.textMuted,
  },

  // Input
  inputRow: {
    paddingHorizontal: PaprikaSpacing.lg,
  },
  input: {
    borderWidth: 1,
    borderColor: Paprika.border,
    borderRadius: PaprikaRadius.md,
    paddingHorizontal: PaprikaSpacing.md,
    paddingVertical: 12,
    ...PaprikaTypography.bodyLarge,
    color: Paprika.textPrimary,
    backgroundColor: Paprika.surfaceLight,
  },

  // Actions
  actions: {
    flexDirection: 'row',
    padding: PaprikaSpacing.lg,
    gap: PaprikaSpacing.sm,
  },
  cancelButton: {
    flex: 1,
    paddingVertical: 12,
    borderRadius: PaprikaRadius.md,
    alignItems: 'center',
    backgroundColor: Paprika.surfaceLight,
  },
  cancelButtonText: {
    ...PaprikaTypography.labelLarge,
    color: Paprika.textSecondary,
  },
  addButton: {
    flex: 1,
    paddingVertical: 12,
    borderRadius: PaprikaRadius.md,
    alignItems: 'center',
    backgroundColor: Paprika.accent,
  },
  addButtonDisabled: {
    backgroundColor: Paprika.border,
  },
  addButtonText: {
    ...PaprikaTypography.labelLarge,
    color: Paprika.background,
  },
  addButtonTextDisabled: {
    color: Paprika.textMuted,
  },
});

export default AddCustomMealModal;
